import { useCallback, type RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type { DagColumnMeta } from '@/types/dag'
import { isCellEditable } from './DagCellRenderer'
import { HIDDEN_ATTRIBUTE_IDS } from './columnConfiguration'
import { useDagTableStore } from '@/stores/useDagTableStore'
import type { RowData } from './useProductsTableState'

const NON_DAG_COLUMNS = new Set(['actions'])

export function useDagTabNavigation(
  gridRef: RefObject<AgGridReact<RowData> | null>,
  columnsMap: Map<string, DagColumnMeta>
) {
  const setFocusedCell = useDagTableStore(s => s.setFocusedCell)
  const setEditingCell = useDagTableStore(s => s.setEditingCell)

  const navigateTab = useCallback(
    (
      rowId: string,
      currentColId: string,
      direction: 'forward' | 'backward'
    ) => {
      const api = gridRef.current?.api
      if (!api) return

      // Get ordered visible columns, filter to DAG-only
      const allCols = api.getAllDisplayedColumns()
      const dagColIds = allCols
        .map(c => c.getColId())
        .filter(id => !NON_DAG_COLUMNS.has(id) && !HIDDEN_ATTRIBUTE_IDS.has(id))

      const currentIdx = dagColIds.indexOf(currentColId)
      if (currentIdx === -1) return

      const nextIdx = direction === 'forward' ? currentIdx + 1 : currentIdx - 1

      // At boundary — do nothing
      if (nextIdx < 0 || nextIdx >= dagColIds.length) return

      const targetColId = dagColIds[nextIdx]

      // Get row data for editability check
      const rowNode = api.getRowNode(rowId)
      const cells = (rowNode?.data as RowData | undefined)?.cells ?? {}
      const meta = columnsMap.get(targetColId)

      // plan_sku_num and description are always editable (special cells, not DAG meta)
      if (
        ['plan_sku_num', 'description'].includes(targetColId) ||
        (meta && isCellEditable(meta, cells))
      ) {
        setEditingCell(rowId, targetColId)
      } else {
        setFocusedCell(rowId, targetColId)
      }
    },
    [gridRef, columnsMap, setFocusedCell, setEditingCell]
  )

  return navigateTab
}
